/**
 * Client-side image compression before upload (POD photos, receipt photos,
 * exception evidence). Downscales and re-encodes as JPEG to keep mobile
 * uploads under the API body limit without a server round-trip. Falls back
 * to the original file's data URL if canvas encoding is unavailable
 * (e.g. non-image files, very old browsers).
 *
 * Default (POD/receipts): 1600px @ 0.7 — small enough for patchy networks.
 * Evidence preset (scan exceptions): 2400px @ 0.85 — keeps labels, seal
 * numbers, and handwriting readable while still fitting under Fastify's
 * body limit after base64 encoding.
 */

const MAX_EDGE_PX = 1600;
const JPEG_QUALITY = 0.7;

/** Higher-fidelity preset for exception / discrepancy evidence photos. */
export const EVIDENCE_COMPRESS_OPTIONS = {
  maxEdgePx: 2400,
  quality: 0.85,
} as const;

function readAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image for compression'));
    img.src = src;
  });
}

export interface CompressImageOptions {
  maxEdgePx?: number;
  quality?: number;
}

/**
 * Compresses an image file/blob to a data URL. Non-image files are passed
 * through unchanged (as a data URL) so callers can use one code path for
 * all file inputs.
 */
export async function compressImage(
  file: File | Blob,
  options: CompressImageOptions = {},
): Promise<string> {
  const maxEdgePx = options.maxEdgePx ?? MAX_EDGE_PX;
  const quality = options.quality ?? JPEG_QUALITY;

  if (!(file.type || '').startsWith('image/')) {
    return readAsDataUrl(file);
  }

  try {
    const originalDataUrl = await readAsDataUrl(file);
    const img = await loadImage(originalDataUrl);

    const scale = Math.min(1, maxEdgePx / Math.max(img.width, img.height));
    const width = Math.max(1, Math.round(img.width * scale));
    const height = Math.max(1, Math.round(img.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return originalDataUrl;

    ctx.drawImage(img, 0, 0, width, height);
    return canvas.toDataURL('image/jpeg', quality);
  } catch {
    // Compression is a best-effort optimization — never block the capture flow on it.
    return readAsDataUrl(file);
  }
}
